import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button } from '@/common'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import EmailInputGroup from '@/shared/components/EmailInputGroup'
import useI18n from '@/shared/hooks/usei18n'
import AuthLayout from '../../components/AuthLayout'
import { useConsumeMagicLink, useRequestMagicLink } from '@/features/auth/hooks/auth'

const MAGIC_LINK_RESEND_COOLDOWN_SEC = 60

export const ERROR_CODE_QUERY_KEY = 'error_code'
export const CODE_QUERY_KEY = 'code'

export type MagicLinkErrorBucket = 'EXPIRED_LINK' | 'INVALID_LINK' | 'GENERIC'

export function getMagicLinkErrorBucket(code: string | null): MagicLinkErrorBucket {
  if (code === 'EXPIRED_LINK') {
    return 'EXPIRED_LINK'
  }
  if (code === 'INVALID_LINK') {
    return 'INVALID_LINK'
  }
  return 'GENERIC'
}

function MagicLinkLandingPage({ code }: { code: string }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { mutateAsync: consumeMagicLink, isPending: isConsumingMagicLink } =
    useConsumeMagicLink()

  const onContinue = useCallback(async () => {
    try {
      const result = await consumeMagicLink({ code })
      navigate(result.redirectPath)
    } catch (error) {
      if (error instanceof Error) {
        navigate(`/magic-link?error_code=${error.message}`)
        return
      }
      navigate('/magic-link?error_code=UNKNOWN_ERROR')
    }
  }, [code, consumeMagicLink, navigate])

  return (
    <AuthLayout showCarousel={false}>
      <div className="flex flex-col items-center space-y-4 p-6 w-full max-w-md">
        <div className="space-y-2 text-center">
          <p className="font-semibold">{t('login.magicLink.landing.title')}</p>
          <p className="text-sm text-gray-600">{t('login.magicLink.landing.description')}</p>
        </div>
        <Button
          type="button"
          className="w-full"
          isLoading={isConsumingMagicLink}
          disabled={isConsumingMagicLink}
          onClick={() => {
            void onContinue()
          }}
        >
          {t('login.magicLink.landing.continueAction')}
        </Button>
      </div>
    </AuthLayout>
  )
}

function MagicLinkLoginErrorPage({
  isAuthenticated,
  errorCode
}: {
  isAuthenticated: boolean
  errorCode: string | null
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [requestError, setRequestError] = useState<string | null>(null)
  const [isLinkSent, setIsLinkSent] = useState(false)
  const [resendCooldownSec, setResendCooldownSec] = useState(0)
  const resendTimerRef = useRef<number | null>(null)
  const { mutateAsync: requestMagicLink, isPending: isSendingMagicLink } = useRequestMagicLink()

  const clearResendTimer = useCallback(() => {
    if (resendTimerRef.current !== null) {
      window.clearInterval(resendTimerRef.current)
      resendTimerRef.current = null
    }
  }, [])

  const startResendCooldown = useCallback(() => {
    clearResendTimer()
    setResendCooldownSec(MAGIC_LINK_RESEND_COOLDOWN_SEC)
    const timer = window.setInterval(() => {
      setResendCooldownSec(prev => {
        if (prev <= 1) {
          window.clearInterval(timer)
          resendTimerRef.current = null
          return 0
        }
        return prev - 1
      })
    }, 1000)
    resendTimerRef.current = timer
  }, [clearResendTimer])

  useEffect(() => () => clearResendTimer(), [clearResendTimer])

  const errorBucket = getMagicLinkErrorBucket(errorCode)
  const isEmailValid = z.email().safeParse(email).success

  const onSendMagicLink = useCallback(async () => {
    if (!isEmailValid) {
      return
    }

    setRequestError(null)
    try {
      await requestMagicLink({ email })
      setIsLinkSent(true)
      startResendCooldown()
    } catch {
      setRequestError('login.magicLink.errors.general')
    }
  }, [email, isEmailValid, requestMagicLink, startResendCooldown])

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      void onSendMagicLink()
    },
    [onSendMagicLink]
  )

  return (
    <AuthLayout showCarousel={false}>
      <div className="flex flex-col items-center space-y-4 p-6 w-full max-w-md">
        <Alert type="error">
          <p className="font-semibold">
            {t(`login.magicLink.redirectErrors.${errorBucket}.title`)}
          </p>
          <p className="mt-1">{t(`login.magicLink.redirectErrors.${errorBucket}.description`)}</p>
        </Alert>

        {isLinkSent && (
          <Alert type="success" className="whitespace-pre-line">
            {t('login.magicLink.sent.detail', { email })}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 w-full">
          <EmailInputGroup
            onChange={evt => setEmail(evt.target.value)}
            value={email}
            name="email"
            label={t('loginForm.fields.email.label')}
          />
          <Button
            type="submit"
            className="w-full"
            isLoading={isSendingMagicLink && resendCooldownSec === 0}
            disabled={isSendingMagicLink || resendCooldownSec > 0 || !isEmailValid}
          >
            {isLinkSent
              ? resendCooldownSec > 0
                ? t('login.magicLink.sent.resendWait', { seconds: resendCooldownSec })
                : t('login.magicLink.sent.resend')
              : t('login.magicLink.request.sendAction')}
          </Button>
        </form>

        {requestError && <Alert type="error">{t(requestError)}</Alert>}

        <div className="text-sm text-center space-y-2 flex flex-col items-center">
          {isAuthenticated && (
            <Button
              variant="subtle"
              className="w-full"
              onClick={() => navigate('/cbam-reporting')}
              type="button"
            >
              {t('login.magicLink.redirectErrors.actions.continueToWorkspace')}
            </Button>
          )}
          {!isAuthenticated && (
            <Link className="text-green-600 underline" to="/login">
              {t('login.magicLink.redirectErrors.actions.backToLogin')}
            </Link>
          )}
        </div>
      </div>
    </AuthLayout>
  )
}

export default function MagicLinkPage({ isAuthenticated }: { isAuthenticated: boolean }) {
  const [searchParams] = useSearchParams()
  const errorCode = searchParams.get(ERROR_CODE_QUERY_KEY)
  const code = searchParams.get(CODE_QUERY_KEY)

  if (!errorCode && code) {
    return <MagicLinkLandingPage code={code} />
  }

  const resolvedErrorCode = errorCode ?? 'INVALID_REQUEST'

  return (
    <MagicLinkLoginErrorPage isAuthenticated={isAuthenticated} errorCode={resolvedErrorCode} />
  )
}
